import { DependencyList, useEffect, useRef } from 'react';
import { isEqual } from '@vkontakte/vkjs';
import { useForceUpdate } from '@djs/react';

/**
 * Возвращает значение value. Но если deps был изменен,
 * то в течение delay миллисекунд будет возвращаться valueInDelay либо предыдущее значение value.
 */
export function useDelayValue<T>(value: T, deps: DependencyList, delay: number, valueInDelay?: T) {
  const forceUpdate = useForceUpdate();

  const timeoutRef = useRef<NodeJS.Timeout>(void 0);
  const delayDeps = useRef(deps);

  if (delay === 0) {
    delayDeps.current = deps;
  }

  const isEqualDeps = isEqual(delayDeps.current, deps);
  const lastEqualDepsValue = useRef(value);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!isEqualDeps) {
      timeoutRef.current = setTimeout(() => {
        delayDeps.current = deps;
        forceUpdate();
      }, delay);

      return () => {
        clearTimeout(timeoutRef.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  lastEqualDepsValue.current = isEqualDeps
    ? value
    : typeof valueInDelay === 'undefined'
      ? lastEqualDepsValue.current
      : valueInDelay;

  return lastEqualDepsValue.current;
}
